import {type CSSProperties, useCallback, useLayoutEffect, useRef, useState} from 'react';

const FADE_PX = 32;

/**
 * /workstreams: fades a horizontal scroller's left / right edge while
 * there's more to scroll that way, so a clipped row reads as "keeps going". Put
 * `ref`, `onScroll`, and `style` on the scroller; `itemCount` re-measures when
 * its contents change.
 */
export const useScrollEdgeFade = <T extends HTMLElement>(itemCount: number) => {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState({start: false, end: false});

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    // 1px of slack for fractional scroll positions.
    const start = element.scrollLeft > 1;
    const end = element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
    setEdges((previous) =>
      previous.start === start && previous.end === end ? previous : {start, end}
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure, itemCount]);

  const mask = `linear-gradient(to right, ${edges.start ? 'transparent' : '#000'}, #000 ${FADE_PX}px, #000 calc(100% - ${FADE_PX}px), ${edges.end ? 'transparent' : '#000'})`;
  const style: CSSProperties | undefined =
    edges.start || edges.end ? {maskImage: mask, WebkitMaskImage: mask} : undefined;

  return {ref, onScroll: measure, style};
};
